import { isNil } from 'lodash';
import { freeze } from '@reduxjs/toolkit';
import useUser from '../useUser';
import { useDashboardEntityPermissionCheck } from './useDashboardEntityPermission';
import { UnreachableCaseError } from 'ts-essentials';
import { useMemo } from 'react';
import usePermission from './usePermission';
import { PermissionKey, QueryType } from '../../apollo/graphqlSchema';
import produce from 'immer';
import {
  IAnalysisEntityPermissions,
  IAnalysisEntityPermissionsHint,
} from './types';
import { PERMISSION_ERROR_MESSAGE } from '../../../components/Chart/constants/utils';
import { IAnalysisState } from '../../../redux/slices/analyses';
import { assertNotNil } from '../../types';

const reportPermissionKeysByEntityPermission = freeze<
  Record<keyof IAnalysisEntityPermissions, PermissionKey[]>
>(
  {
    editAnalysis: [PermissionKey.AnalysisEdit],
    duplicateAnalysis: [PermissionKey.AnalysisCreate],
    createSchedule: [PermissionKey.AnalysisScheduleCreate],
    replaceAnalysisInContainer: [],
    deleteAnalysisInContainer: [],
    duplicateAnalysisInContainer: [],
  },
  true,
);

const cohortPermissionKeysByEntityPermission = freeze<
  Record<keyof IAnalysisEntityPermissions, PermissionKey[]>
>(
  {
    editAnalysis: [PermissionKey.CohortEdit],
    duplicateAnalysis: [PermissionKey.CohortReportCreate],
    createSchedule: [PermissionKey.CohortBrazeExportCreate],
    // inherited from the container, should be already checked for permissions
    replaceAnalysisInContainer: [],
    deleteAnalysisInContainer: [],
    duplicateAnalysisInContainer: [],
  },
  true,
);

const ownerPermission = freeze<IAnalysisEntityPermissions>(
  {
    editAnalysis: true,
    duplicateAnalysis: true,
    createSchedule: true,
    replaceAnalysisInContainer: true,
    deleteAnalysisInContainer: true,
    duplicateAnalysisInContainer: true,
  },
  true,
);

const defaultPermission = freeze<IAnalysisEntityPermissions>(
  {
    editAnalysis: false,
    duplicateAnalysis: true, // we allow anyone to duplicate (as long they have the permission)
    createSchedule: false,
    replaceAnalysisInContainer: false,
    deleteAnalysisInContainer: false,
    duplicateAnalysisInContainer: false,
  },
  true,
);

const createPermissionsHint = (
  entityPermissions: IAnalysisEntityPermissions,
  roleName: string,
) => {
  const hints: IAnalysisEntityPermissionsHint = {};
  Object.keys(entityPermissions).forEach((untypedKey) => {
    const entityPermission = untypedKey as keyof typeof entityPermissions;
    if (!entityPermissions[entityPermission]) {
      hints[entityPermission] =
        `This action is not allowed because you have ${roleName} access`;
    }
  });
  return hints;
};

const useAnalysisEntityPermission = (analysis: IAnalysisState) => {
  const loggedInUser = useUser();
  const { getDashboardEntityPermissions } = useDashboardEntityPermissionCheck();
  const { checkIsPermitted } = usePermission();
  const { metadata } = analysis;
  assertNotNil(metadata);
  const { creator, container, queryType } = metadata;
  const { analysisEntityPermissions, analysisEntityPermissionsHint } = useMemo<{
    analysisEntityPermissions: IAnalysisEntityPermissions;
    analysisEntityPermissionsHint: IAnalysisEntityPermissionsHint;
  }>(() => {
    let analysisEntityPermissions: IAnalysisEntityPermissions;
    let analysisEntityPermissionsHint: IAnalysisEntityPermissionsHint;
    if (creator.id === loggedInUser.id) {
      analysisEntityPermissions = ownerPermission;
      analysisEntityPermissionsHint = createPermissionsHint(
        analysisEntityPermissions,
        'creator',
      );
    } else {
      analysisEntityPermissions = defaultPermission;
      analysisEntityPermissionsHint = createPermissionsHint(
        analysisEntityPermissions,
        'view-only',
      );
    }

    if (!isNil(container)) {
      switch (container.__typename) {
        case 'AnalysisDashboardContainer': {
          const { dashboardEntityPermissions, dashboardEntityPermissionsHint } =
            getDashboardEntityPermissions(container);
          return {
            analysisEntityPermissions: {
              editAnalysis: dashboardEntityPermissions.editReport,
              replaceAnalysisInContainer:
                dashboardEntityPermissions.replaceReport,
              deleteAnalysisInContainer:
                dashboardEntityPermissions.deleteReport,
              duplicateAnalysisInContainer:
                dashboardEntityPermissions.duplicateReport,
              duplicateAnalysis: true,
              createSchedule: dashboardEntityPermissions.editReport,
            },
            analysisEntityPermissionsHint: {
              editAnalysis: dashboardEntityPermissionsHint.editReport,
              replaceAnalysisInContainer:
                dashboardEntityPermissionsHint.replaceReport,
              deleteAnalysisInContainer:
                dashboardEntityPermissionsHint.deleteReport,
              duplicateAnalysisInContainer:
                dashboardEntityPermissionsHint.duplicateReport,
              duplicateAnalysis: undefined,
              createSchedule: dashboardEntityPermissionsHint.editReport,
            },
          };
        }
        case 'AnalysisWorkspaceContainer': {
          return {
            analysisEntityPermissions,
            analysisEntityPermissionsHint,
          };
        }
        default:
          throw new UnreachableCaseError(container);
      }
    } else {
      return {
        analysisEntityPermissions,
        analysisEntityPermissionsHint,
      };
    }
  }, [container, creator.id, getDashboardEntityPermissions, loggedInUser.id]);

  return useMemo(
    () =>
      produce(
        {
          entityPermissions: analysisEntityPermissions,
          entityPermissionsHint: analysisEntityPermissionsHint,
        },
        ({
          entityPermissions: entityPermissionsDraft,
          entityPermissionsHint: entityPermissionsHintDraft,
        }) => {
          Object.keys(entityPermissionsDraft).forEach((untypedKey) => {
            const entityPermission =
              untypedKey as keyof typeof entityPermissionsDraft;

            const requiredPermissionKeys =
              queryType === QueryType.CohortSample
                ? cohortPermissionKeysByEntityPermission[entityPermission]
                : reportPermissionKeysByEntityPermission[entityPermission];
            if (
              entityPermissionsDraft[entityPermission] &&
              requiredPermissionKeys.length > 0
            ) {
              entityPermissionsDraft[entityPermission] = checkIsPermitted({
                requiredPermissionKeys,
              });
              if (!entityPermissionsDraft[entityPermission]) {
                entityPermissionsHintDraft[entityPermission] =
                  PERMISSION_ERROR_MESSAGE;
              }
            }
          });
        },
      ),
    [
      analysisEntityPermissions,
      analysisEntityPermissionsHint,
      queryType,
      checkIsPermitted,
    ],
  );
};

export default useAnalysisEntityPermission;
